import React from 'react';
import {
  Chip,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Block as RevokeIcon,
  CloudDone as InstallIcon,
} from '@mui/icons-material';
import { License } from '../../services/onpremApi';

export const STATUS_LABELS: Record<string, { label: string; color: 'default' | 'success' | 'warning' | 'error' }> = {
  unused: { label: 'Sin usar', color: 'default' },
  active: { label: 'Activa', color: 'success' },
  suspended: { label: 'Suspendida', color: 'warning' },
  revoked: { label: 'Revocada', color: 'error' },
};

interface Props {
  licenses: License[];
  onRevoke: (license: License) => void;
  onShowInstall: (license: License) => void;
}

const formatDate = (iso?: string | null) => (iso ? iso.slice(0, 10) : '—');

const LicenseTable: React.FC<Props> = ({ licenses, onRevoke, onShowInstall }) => (
  <TableContainer component={Paper}>
    <Table size="small" stickyHeader>
      <TableHead>
        <TableRow>
          <TableCell sx={{ fontWeight: 'bold' }}>Clave</TableCell>
          <TableCell sx={{ fontWeight: 'bold' }}>Cliente</TableCell>
          <TableCell sx={{ fontWeight: 'bold' }}>Plan</TableCell>
          <TableCell sx={{ fontWeight: 'bold' }}>Estado</TableCell>
          <TableCell align="right" sx={{ fontWeight: 'bold' }}>Equipos</TableCell>
          <TableCell align="right" sx={{ fontWeight: 'bold' }}>Usuarios</TableCell>
          <TableCell sx={{ fontWeight: 'bold' }}>Activada</TableCell>
          <TableCell sx={{ fontWeight: 'bold' }}>Vence</TableCell>
          <TableCell sx={{ fontWeight: 'bold' }}>Notas</TableCell>
          <TableCell align="right" sx={{ fontWeight: 'bold' }}>Acciones</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {licenses.length === 0 ? (
          <TableRow>
            <TableCell colSpan={10} align="center" sx={{ py: 4 }}>
              <Typography color="text.secondary">Sin licencias</Typography>
            </TableCell>
          </TableRow>
        ) : (
          licenses.map((lic) => {
            const status = STATUS_LABELS[lic.status] ?? { label: lic.status, color: 'default' as const };
            const revoked = lic.status === 'revoked';
            return (
              <TableRow key={lic.id} hover sx={{ opacity: revoked ? 0.6 : 1 }}>
                <TableCell>
                  <Typography component="code" sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                    {lic.license_key}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">{lic.client_id}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {lic.organization_name}
                  </Typography>
                </TableCell>
                <TableCell>{lic.plan}</TableCell>
                <TableCell>
                  <Chip label={status.label} color={status.color} size="small" />
                </TableCell>
                <TableCell align="right">{lic.max_devices}</TableCell>
                <TableCell align="right">{lic.max_users}</TableCell>
                <TableCell>{formatDate(lic.activated_at)}</TableCell>
                <TableCell>{formatDate(lic.expires_at)}</TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary" noWrap sx={{ maxWidth: 220 }}>
                    {lic.notes || '—'}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  {lic.status === 'active' && lic.install_id && (
                    <Tooltip title="Ver instalación">
                      <IconButton
                        size="small"
                        aria-label={`Ver instalación de ${lic.client_id}`}
                        onClick={() => onShowInstall(lic)}
                      >
                        <InstallIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                  {!revoked && (
                    <Tooltip title="Revocar">
                      <IconButton
                        size="small"
                        color="error"
                        aria-label={`Revocar licencia ${lic.license_key}`}
                        onClick={() => onRevoke(lic)}
                      >
                        <RevokeIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </TableCell>
              </TableRow>
            );
          })
        )}
      </TableBody>
    </Table>
  </TableContainer>
);

export default LicenseTable;
